import { useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import type { EditProjectUserProfileBaseline, EditUserUnsavedWorkState } from './editProjectUserTypes';
import { normalizeUserProjectsForEquality } from '../../userProjectAccessMutations';
import type { EditProjectUserFormState } from '../../state/userManagementSlice';

/** Compared against baseline for “profile changed” detection (phone handled separately with country code) */
const profileFieldKeys = ['firstName', 'lastName'] as const;

type FormState = EditProjectUserFormState['formState'];

type Params = {
    formState: FormState;
    customerId: string;
    currentCustomerProjectIds: string[];
    isActiveOrInvited: boolean;
    inFlightSitePermissionSaves: number;
    isSubmitting: boolean;
    onUnsavedWorkStateChange?: (state: EditUserUnsavedWorkState) => void;
};

/**
 * Tracks profile and site-permission baselines (mutable refs + version counters) and derives unsaved-change flags
 * for the edit-user drawer. Refs hold last server-aligned values; bumping versions forces memos to recompute after save.
 */
export function useEditProjectUserUnsavedChanges({
    formState,
    customerId,
    currentCustomerProjectIds,
    isActiveOrInvited,
    inFlightSitePermissionSaves,
    isSubmitting,
    onUnsavedWorkStateChange,
}: Params) {
    /** Roles loaded from API for this customer — compared to `formState.roles` for “admin roles changed” */
    const savedOrganisationRoles = formState?.customers.find(c => c.customerId === customerId)?.roles;
    /** Last known persisted project+role rows (updated after save / matrix persist) for dirty detection */
    const lastPersistedSiteAssignmentsRef = useRef<{ projectId: string; roles: string[] | null }[] | null>(null);

    /** Snapshot of profile fields after last successful save (or on open) — parent may reset after save */
    const profileBaselineRef = useRef<EditProjectUserProfileBaseline | null>(null);
    /** Detects user switch in the same modal session so baseline is re-seeded */
    const profileBaselineCognitoKeyRef = useRef<string | undefined>(undefined);
    if (formState.cognitoId !== profileBaselineCognitoKeyRef.current) {
        profileBaselineCognitoKeyRef.current = formState.cognitoId || undefined;
        if (formState.cognitoId) {
            profileBaselineRef.current = {
                firstName: formState.firstName ?? '',
                lastName: formState.lastName ?? '',
                phone: formState.phone ?? '',
                phoneCountryCode: formState.phoneCountryCode ?? '',
                notify: formState.notify,
            };
        } else {
            profileBaselineRef.current = null;
        }
    }

    /** Incremented when site assignments baseline updates so `siteAssignmentsChanged` memos re-run */
    const [persistedAssignmentsVersion, setPersistedAssignmentsVersion] = useState(0);
    /** Incremented when profile baseline updates after save */
    const [profileBaselineVersion, setProfileBaselineVersion] = useState(0);

    useEffect(() => {
        if (!lastPersistedSiteAssignmentsRef.current) {
            lastPersistedSiteAssignmentsRef.current = formState.projects;
            setPersistedAssignmentsVersion(v => v + 1);
        }
    }, [formState.projects]);

    const isProfilePhoneValid = useMemo(() => !isNaN(Number(formState.phone)) && isFinite(Number(formState.phone)), [formState.phone]);

    const profileSaveDisabled = useMemo(() => {
        const baseline = profileBaselineRef.current;
        if (!baseline || !formState.cognitoId) {
            return true;
        }
        const namesMatch = isEqual(pick(formState, profileFieldKeys), pick(baseline, profileFieldKeys));
        const phoneMatch = isEqual(
            { phone: formState.phone ?? '', phoneCountryCode: formState.phoneCountryCode ?? '' },
            { phone: baseline.phone, phoneCountryCode: baseline.phoneCountryCode ?? '' }
        );
        const notifyMatch = formState.notify === baseline.notify;
        return Boolean(!isProfilePhoneValid || (namesMatch && phoneMatch && notifyMatch));
    }, [
        formState.phone,
        formState.firstName,
        formState.lastName,
        formState.phoneCountryCode,
        formState.cognitoId,
        formState.notify,
        isProfilePhoneValid,
        profileBaselineVersion,
    ]);

    const organisationRolesChanged = useMemo(() => {
        const defaults = [...(savedOrganisationRoles || [])].sort();
        const current = [...(formState.roles || [])].sort();
        return !isEqual(defaults, current);
    }, [savedOrganisationRoles, formState.roles]);

    const profileFieldsChangedSinceLastSave = useMemo(() => {
        const baseline = profileBaselineRef.current;
        if (!baseline || !formState.cognitoId) {
            return false;
        }
        if (!isProfilePhoneValid) {
            return false;
        }
        const namesMatch = isEqual(pick(formState, profileFieldKeys), pick(baseline, profileFieldKeys));
        const phoneMatch = isEqual(
            { phone: formState.phone ?? '', phoneCountryCode: formState.phoneCountryCode ?? '' },
            { phone: baseline.phone, phoneCountryCode: baseline.phoneCountryCode ?? '' }
        );
        const notifyMatch = formState.notify === baseline.notify;
        return !(namesMatch && phoneMatch && notifyMatch);
    }, [
        formState.phone,
        formState.firstName,
        formState.lastName,
        formState.phoneCountryCode,
        formState.cognitoId,
        formState.notify,
        isProfilePhoneValid,
        profileBaselineVersion,
    ]);

    /** Like profileFieldsChangedSinceLastSave but does not require valid phone — used for “discard on close?” */
    const profileEditsNeedCloseWarning = useMemo(() => {
        const baseline = profileBaselineRef.current;
        if (!baseline || !formState.cognitoId) {
            return false;
        }
        const namesMatch = isEqual(pick(formState, profileFieldKeys), pick(baseline, profileFieldKeys));
        const phoneMatch = isEqual(
            { phone: formState.phone ?? '', phoneCountryCode: formState.phoneCountryCode ?? '' },
            { phone: baseline.phone, phoneCountryCode: baseline.phoneCountryCode ?? '' }
        );
        const notifyMatch = formState.notify === baseline.notify;
        return !(namesMatch && phoneMatch && notifyMatch);
    }, [
        formState.phone,
        formState.firstName,
        formState.lastName,
        formState.phoneCountryCode,
        formState.cognitoId,
        formState.notify,
        profileBaselineVersion,
    ]);

    const siteAssignmentsChanged = useMemo(() => {
        if (!formState.cognitoId) {
            return false;
        }
        const refList = lastPersistedSiteAssignmentsRef.current ?? [];
        return !isEqual(
            normalizeUserProjectsForEquality(formState.projects, currentCustomerProjectIds),
            normalizeUserProjectsForEquality(refList, currentCustomerProjectIds)
        );
    }, [formState.projects, formState.cognitoId, currentCustomerProjectIds, persistedAssignmentsVersion]);

    const hasUnsavedMainViewEdits = siteAssignmentsChanged || organisationRolesChanged || profileFieldsChangedSinceLastSave;
    const showSaveOnDefaultView = isActiveOrInvited && hasUnsavedMainViewEdits;
    const shouldWarnOnClose = siteAssignmentsChanged || organisationRolesChanged || profileEditsNeedCloseWarning;

    useEffect(() => {
        onUnsavedWorkStateChange?.({
            hasUnsavedChanges: shouldWarnOnClose,
            sitePermissionsSaveInProgress: inFlightSitePermissionSaves > 0,
            profileSaveInProgress: isSubmitting,
        });
    }, [shouldWarnOnClose, inFlightSitePermissionSaves, isSubmitting, onUnsavedWorkStateChange]);

    return {
        savedOrganisationRoles,
        isProfilePhoneValid,
        profileSaveDisabled,
        organisationRolesChanged,
        profileFieldsChangedSinceLastSave,
        profileEditsNeedCloseWarning,
        siteAssignmentsChanged,
        hasUnsavedMainViewEdits,
        showSaveOnDefaultView,
        shouldWarnOnClose,
        lastPersistedSiteAssignmentsRef,
        profileBaselineRef,
        setPersistedAssignmentsVersion,
        setProfileBaselineVersion,
    };
}
